import { Conversation } from '@botpress/hitl-client'
import { AsyncButton, Button, showSuccessToast } from '@bpinternal/ui-kit-next'
import {
  useQuery,
  useQueryClient,
  useSuspenseInfiniteQuery,
  useSuspenseQueries,
  useSuspenseQuery,
} from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { Check } from 'lucide-react'
import { FC, useEffect } from 'react'
import { match } from 'ts-pattern'
import { z } from 'zod'
import HITLMessage from '~/assets/hitl-message.svg?react'
import { Boundary, Page } from '~/components'
import { EmptyState, Icon } from '~/elements'
import { isLegacyBilling } from '~/features/billing/utils/billingVersion'
import { ConversationDetails, ConversationList, HitlDeskPromo } from '~/features/hitl/components'
import { HITLTabs } from '~/features/hitl/components/HITLTabs'
import { useFeature, useQueryKeys, useUpdateBot } from '~/hooks'
import { SidebarLayout } from '~/layouts'
import { conversationQueryOptions, getIntegrationByNameQueryOptions, listConversationsInfiniteQuery } from '~/queries'
import { getQueryOptions } from '~/services'
import { getApiClient } from '~/stores'
import { cn } from '~/utils'

type ConversationPage = { conversations: unknown[]; meta: { nextToken?: string | null } }
type InfiniteConversationData = { pages: ConversationPage[]; pageParams: unknown[] }

type RawConversation = { tags: Record<string, string | undefined> } & Record<string, unknown>

const enrichConversation = (conversation: RawConversation): Conversation =>
  ({
    ...conversation,
    status: conversation.tags['hitl:status'] as Conversation['status'],
    assignee: conversation.tags['hitl:assigneeWorkspaceMemberId']
      ? {
          id: conversation.tags['hitl:assigneeUserId'] ?? '',
          workspaceMemberId: conversation.tags['hitl:assigneeWorkspaceMemberId'],
        }
      : undefined,
    requester: conversation.tags['hitl:requesterUserId']
      ? {
          id: conversation.tags['hitl:requesterUserId'],
        }
      : undefined,
    assignedAt: conversation.tags['hitl:assignedAt'] ?? '',
  }) as Conversation

const paramsSchema = z.object({
  conversationId: z
    .string()
    .optional()
    .catch(() => undefined),
})

export const Route = createFileRoute('/workspaces/$workspaceId/bots/$botId/hitl')({
  component: Component,
  validateSearch: paramsSchema,
})

function Component() {
  const { botId, workspaceId } = Route.useParams()

  const [{ data: workspace }, { data: bot }] = useSuspenseQueries({
    queries: [
      getQueryOptions('workspaces_/$workspaceId_', { workspaceId }),
      getQueryOptions('workspaces_/$workspaceId_/bots_/$botId_', { workspaceId, botId }),
    ],
  })

  const { plan } = workspace
  const isWhiteListed = useFeature('hitlWhitelist')
  const isDeskRedirectEnabled = useFeature('desk-billing-v4-redirection')
  const hasV4HitlAccess = useFeature('v4-hitl-access')
  const showDeskPromo = isDeskRedirectEnabled && !isLegacyBilling(workspace) && !hasV4HitlAccess

  const integrationsByName = Object.entries(bot.integrations).map(([intId, int]) => ({ ...int, id: intId }))
  const hitlIntegration = integrationsByName.find((int) => int.name === 'hitl')
  const integrationInstalled = !!(hitlIntegration && hitlIntegration.enabled)

  return (
    <Page
      title={showDeskPromo ? undefined : 'Human Handoff'}
      className={cn('@container', showDeskPromo && 'max-w-none')}
    >
      {showDeskPromo ? (
        <HitlDeskPromo workspaceId={workspaceId} botId={botId} />
      ) : (
        match({
          plan,
          isWhiteListed,
          integrationInstalled,
        })
          // Case: Community plan + not whitelisted
          .with({ plan: 'community', isWhiteListed: false }, () => <UpgradePlan />)
          // Case: Community/Team/Ent plan + HITL not installed
          .with({ integrationInstalled: false }, () => <InstallHITL />)
          // Case: Community/Team/Ent plan + HITL installed
          .with({ integrationInstalled: true }, () => <HITLDashboard />)
          .exhaustive()
      )}
    </Page>
  )
}

const UpgradePlan = () => {
  const { workspaceId } = Route.useParams()

  return (
    <div className="flex h-[55vh] w-full items-center justify-center">
      <div className={'flex items-center rounded-md border border-neutral-300 p-16'} style={{ width: '877px' }}>
        <div className={'flex w-1/2 flex-col gap-6'}>
          <div className="text-2xl font-medium">Take over conversations from the dashboard.</div>
          <div>
            {[
              'Identify escalation criteria',
              'Escalate conversations to human agents',
              'Interact directly with end users',
              'Assign conversations to specific agent',
              'Filter conversations by channel or integration',
              'Pass conversations back to AI agents',
            ].map((feature) => (
              <div key={feature} className="flex items-center gap-2">
                <Icon icon={Check} color="green" />
                <span className="text-neutral-900">{feature}</span>
              </div>
            ))}
          </div>
          <div className="flex gap-3">
            <Link to="/workspaces/$workspaceId/billing" params={{ workspaceId: workspaceId }}>
              <Button className="bg-green-500 hover:bg-green-700">Upgrade Plan</Button>
            </Link>
          </div>
        </div>
        <HITLMessage className={'w-1/2'} />
      </div>
    </div>
  )
}

const InstallHITL: FC = () => {
  const { botId, workspaceId } = Route.useParams()
  const { isPending: isBotPending, mutate: updateBot } = useUpdateBot()

  const { data: hitlIntegration } = useSuspenseQuery(getIntegrationByNameQueryOptions({ name: 'hitl', workspaceId }))

  const installHITLIntegration = async () => {
    updateBot({
      id: botId,
      workspaceId,
      integrations: {
        [hitlIntegration.id]: { enabled: true, configuration: {} },
      },
      onSuccess: async () => {
        showSuccessToast('HITL integration installed.')
      },
    })
  }

  return (
    <div className="flex h-[55vh] w-full items-center justify-center">
      <EmptyState
        className="m-auto"
        title="Missing HITL Integration"
        description={
          <span>
            <strong>Human Handoff</strong> requires an integration to be installed and enabled.
          </span>
        }
        primaryAction={
          <AsyncButton loading={isBotPending} onClick={installHITLIntegration}>
            <span>Install & enable HITL</span>
          </AsyncButton>
        }
      />
    </div>
  )
}

const HITLDashboard: FC = () => {
  const { botId, workspaceId } = Route.useParams()
  const { conversationId } = Route.useSearch()

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useSuspenseInfiniteQuery(
    listConversationsInfiniteQuery({ botId, workspaceId, integrationName: 'hitl' })
  )

  const queryClient = useQueryClient()
  const { listConversations: listConversationsKey } = useQueryKeys()
  const infiniteKey = listConversationsKey({ botId, workspaceId, integrationName: 'hitl' })

  const { data: firstPage } = useQuery({
    queryKey: [...infiniteKey, 'first-page-poll'],
    queryFn: () =>
      getApiClient({ workspaceId, botId }).listConversations({
        sortField: 'updatedAt',
        sortDirection: 'desc',
        integrationName: 'hitl',
      }),
    refetchInterval: 5_000,
    refetchIntervalInBackground: false,
    staleTime: 0,
    meta: { suppressError: true },
  })

  useEffect(() => {
    if (!firstPage) return
    const firstPageIds = new Set(firstPage.conversations.map((c) => c.id))
    queryClient.setQueryData<InfiniteConversationData>(infiniteKey, (prev) => {
      if (!prev?.pages.length) return prev
      return {
        ...prev,
        pages: [
          firstPage as ConversationPage,
          ...prev.pages.slice(1).map((page) => ({
            ...page,
            conversations: page.conversations.filter((c) => !firstPageIds.has((c as { id: string }).id)),
          })),
        ],
      }
    })
    // infiniteKey is an array that changes identity per render; rely on firstPage changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstPage, queryClient])

  const fetchedConversations = data.pages.flatMap((page) => page.conversations)

  const allConversations: Conversation[] = fetchedConversations.map((conversation) =>
    enrichConversation(conversation as unknown as RawConversation)
  )

  const listHit = conversationId ? allConversations.find((conv) => conv.id === conversationId) : undefined

  const { data: deepLinkData } = useQuery({
    ...conversationQueryOptions({ botId, workspaceId, conversationId: conversationId ?? '' }),
    enabled: !!conversationId && !listHit,
  })

  const currentConversation =
    listHit ??
    (deepLinkData?.conversation
      ? enrichConversation(deepLinkData.conversation as unknown as RawConversation)
      : undefined)

  return (
    <SidebarLayout
      main={
        <Boundary loader={null}>
          {allConversations.length === 0 ? (
            <div className="flex h-[55vh] w-full items-center justify-center">
              <EmptyState
                className="m-auto"
                title="No Conversations"
                description="No conversations have been escalated yet, your bot is doing a great job!"
              />
            </div>
          ) : currentConversation ? (
            <HITLTabs botId={botId} workspaceId={workspaceId} conversation={currentConversation} />
          ) : (
            <div className="flex h-[55vh] w-full items-center justify-center">
              <EmptyState
                className="m-auto"
                title="No conversation selected"
                description="Select a conversation from the list to start helping your users."
              />
            </div>
          )}
        </Boundary>
      }
      leftSidebarSize="3"
      leftSidebar={
        allConversations.length > 0 && (
          <>
            <ConversationList
              allConversationsProps={allConversations}
              botId={botId}
              workspaceId={workspaceId}
              currentConversationId={conversationId}
              fetchNextPage={fetchNextPage}
              hasNextPage={hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
              className={cn({
                'h-[calc(75vh-18rem)] @6xl:h-[calc(100vh-18rem)]': !!currentConversation,
                'h-[calc(100vh-18rem)]': !currentConversation,
              })}
            />
            {currentConversation && (
              <div className="mb-4 @2xl:hidden">
                <ConversationDetails
                  botId={botId}
                  workspaceId={workspaceId}
                  currentConversation={currentConversation}
                />
              </div>
            )}
          </>
        )
      }
      rightSidebar={
        currentConversation && (
          <ConversationDetails botId={botId} workspaceId={workspaceId} currentConversation={currentConversation} />
        )
      }
      rightSidebarClassName="hidden @2xl:flex"
    />
  )
}
